import { GetStateStorage } from "common/storage";
import React, { useState, useRef, useEffect } from "react";
import { MailProDialog } from "views/Pma/MailPro/components/MailProDialog/MailProDialog";
import { MailProDialogHeader } from "views/Pma/MailPro/components/MailProDialog/MailProDialogHeader";
import { MailProDialogBody } from "views/Pma/MailPro/components/MailProDialog/MailProDialogBody";
import Constants from "common/constants";
import { Button } from "@mui/material";
import GetAppIcon from "@mui/icons-material/GetApp";
import {
  LOG_MODULES,
  logFreshdeskSubmitFailed,
  logFreshdeskTicketRejected,
  logFreshdeskValidationFailed,
} from "common/errorTracker";
import {
  appendFreshdeskTicketDefaults,
  getTeamMemberEmails,
  submitFreshdeskTicket,
} from "common/freshdesk";

const BackPageFreshdeskUploadButton = ({
  children,
  currentArea = {},
  showAllEditButtons = false,
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [formData, setFormData] = useState({
    subject: "",
    description: "",
    file: null,
  });
  const [availableAreas, setAvailableAreas] = useState([]);
  const [selectedAreaIDs, setSelectedAreaIDs] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const dialogContentRef = useRef(null);

  useEffect(() => {
    if (message.type && dialogContentRef.current) {
      const scrollable = dialogContentRef.current.parentElement;
      if (scrollable) {
        scrollable.scrollTo({ top: 0, behavior: "smooth" });
      }
    }
  }, [message.type]);

  const getNavbarAreas = (agentData = {}) => {
    const navbarInfo = Array.isArray(agentData.navbarInfo)
      ? agentData.navbarInfo
      : [];
    return navbarInfo
      .filter((area) => area && area.mlsNeighborhood)
      .map((area) => ({
        mlsAreaID: area.mlsAreaID,
        mlsNeighborhood: area.mlsNeighborhood,
      }));
  };

  const openDialog = () => {
    const stateData = GetStateStorage();
    const agentData = (stateData || {}).userData || {};
    const countyID = agentData.countyID || null;
    const countyName = countyID
      ? Constants.COUNTY_ID_NAME_MAP[String(countyID)] ||
        `County ID: ${countyID}`
      : null;

    const subjectParts = [
      `Custom Back Page Photo Request`,
      currentArea.mlsNeighborhood,
      currentArea.mlsCityName,
      countyName,
    ].filter(Boolean);

    const areas = getNavbarAreas(agentData);
    setAvailableAreas(areas);
    setSelectedAreaIDs(
      currentArea.mlsAreaID
        ? [currentArea.mlsAreaID]
        : areas.length === 1
          ? [areas[0].mlsAreaID]
          : [],
    );

    setFormData({
      subject: subjectParts.join(" | "),
      description: "Please upload a custom back page photo for the PMA.",
      file: null,
    });
    setMessage({ type: "", text: "" });
    setIsDragOver(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      subject: "",
      description: "",
      file: null,
    });
    setAvailableAreas([]);
    setSelectedAreaIDs([]);
    setMessage({ type: "", text: "" });
    setIsDragOver(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const toggleAreaSelection = (mlsAreaID) => {
    setSelectedAreaIDs((prev) =>
      prev.includes(mlsAreaID)
        ? prev.filter((id) => id !== mlsAreaID)
        : [...prev, mlsAreaID],
    );
  };

  const getSelectedNeighborhoodNames = () =>
    availableAreas
      .filter((area) => selectedAreaIDs.includes(area.mlsAreaID))
      .map((area) => area.mlsNeighborhood);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith("image/")) {
      setFormData((prev) => ({
        ...prev,
        file: file,
      }));
      setMessage({ type: "", text: "" });
    } else {
      logFreshdeskValidationFailed({
        module: LOG_MODULES.BACK_PAGE_UPLOAD,
        reason: "invalid_file_type_select",
        currentArea,
      });
      setMessage({
        type: "error",
        text: "Please select an image file only (JPG or PNG).",
      });
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.type.startsWith("image/")) {
        setFormData((prev) => ({
          ...prev,
          file: file,
        }));
        setMessage({ type: "", text: "" });
      } else {
        logFreshdeskValidationFailed({
          module: LOG_MODULES.BACK_PAGE_UPLOAD,
          reason: "invalid_file_type_drop",
          currentArea,
        });
        setMessage({
          type: "error",
          text: "Please drop an image file only (JPG or PNG).",
        });
      }
    }
  };

  const downloadSpecsPDF = () => {
    const pdfUrl =
      "https://d2onbxnrte2nim.cloudfront.net/non-covers/ListGen-2026_CUSTOM-BACK_Cover-SPECs.pdf";
    const link = document.createElement("a");
    link.href = pdfUrl;
    link.download = "ListGen-2026_Custom-BACK_Cover_HalfPage-SPECs.pdf";
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const submitTicket = async () => {
    if (!formData.file) {
      setMessage({
        type: "error",
        text: "Please select an image to upload.",
      });
      return;
    }

    const selectedNeighborhoods = getSelectedNeighborhoodNames();
    if (selectedNeighborhoods.length === 0) {
      setMessage({
        type: "error",
        text: "Please select at least one area for this photo.",
      });
      return;
    }

    setIsSubmitting(true);
    setMessage({ type: "", text: "" });
    const stateData = GetStateStorage();
    const agentData = (stateData || {}).userData || {};
    const selectedNeighborhoodsList = selectedNeighborhoods.join(", ");

    try {
      const formDataToSend = new FormData();
      const teamMemberEmails = getTeamMemberEmails(agentData);

      formDataToSend.append(
        "name",
        agentData.listgenUserFirstName + " " + agentData.listgenUserLastName,
      );
      formDataToSend.append("email", agentData.emailAddress);
      formDataToSend.append("subject", formData.subject);

      const fullDescription = `${
        formData.description
      }\n\nApply to areas: ${selectedNeighborhoodsList}\n\nAgent Information:\n- Listgen User ID: ${
        agentData.listgenUserID
      }\n- MLS Neighborhoods: ${selectedNeighborhoodsList}\n- Current MLS Area ID: ${
        currentArea.mlsAreaID || "N/A"
      }`;
      formDataToSend.append("description", fullDescription);

      teamMemberEmails.forEach((email) => {
        formDataToSend.append("cc_emails[]", email);
      });

      appendFreshdeskTicketDefaults(formDataToSend);
      formDataToSend.append("attachments[]", formData.file);

      await submitFreshdeskTicket(formDataToSend);

      const ccCount = teamMemberEmails.length;
      const teamNotificationText =
        ccCount > 0 ? ` Your team (${ccCount} members) has been notified.` : "";
      setMessage({
        type: "success",
        text: `Thank you! Your custom back page photo request has been submitted.${teamNotificationText}`,
      });
      setTimeout(() => {
        setFormData({
          subject: "",
          description: "",
          file: null,
        });
        setSelectedAreaIDs([]);
        setIsDragOver(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }, 2000);
    } catch (error) {
      if (error.response) {
        logFreshdeskTicketRejected({
          module: LOG_MODULES.BACK_PAGE_UPLOAD,
          response: error.response,
          currentArea,
          hasAttachment: Boolean(formData.file),
          agentData,
        });
        setMessage({
          type: "error",
          text: "Failed to upload back page photo. Please try again.",
        });
        return;
      }
      logFreshdeskSubmitFailed({
        module: LOG_MODULES.BACK_PAGE_UPLOAD,
        error,
        currentArea,
        hasAttachment: Boolean(formData.file),
        agentData,
      });
      setMessage({
        type: "error",
        text: "Failed to upload back page photo. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <style>
        {`
                    @keyframes successPulse {
                        0% { transform: scale(1); }
                        50% { transform: scale(1.02); }
                        100% { transform: scale(1); }
                    }
                `}
      </style>
      {showAllEditButtons && (
        <div onClick={openDialog}>
          {children || (
            <div className="backpage-upload-button" role="button" tabIndex={0}>
              <p>Click here to submit a</p>
              <p>custom back page photo</p>
            </div>
          )}
        </div>
      )}

      <MailProDialog
        open={isDialogOpen}
        onClose={closeDialog}
        aria-labelledby="back-page-freshdesk-upload-dialog-title"
      >
        <MailProDialogHeader>
          <h2
            id="back-page-freshdesk-upload-dialog-title"
            style={{
              fontFamily: "objektiv-mk2, sans-serif",
              color: "white",
              fontSize: "18px",
              fontWeight: "500",
              textAlign: "center",
              marginTop: "8px",
              lineHeight: "1.4",
              display: "block",
            }}
          >
            <span style={{ display: "block" }}>Custom Back Cover</span>
            <span style={{ display: "block" }}>Photo Upload</span>
          </h2>
          <p
            style={{
              fontFamily: "objektiv-mk2, sans-serif",
              color: "white",
              fontSize: "14px",
              fontWeight: "300",
              textAlign: "center",
              margin: "8px 0 8px 0",
              opacity: "0.9",
              lineHeight: "1.5",
            }}
          >
            <span style={{ display: "block" }}>
              By uploading a custom image,
            </span>
            <span style={{ display: "block" }}>
              you confirm that you own or have
            </span>
            <span style={{ display: "block" }}>
              permission to use it for printed materials.
            </span>
          </p>
        </MailProDialogHeader>
        <MailProDialogBody>
          <div
            ref={dialogContentRef}
            style={{
              padding: "20px",
              fontFamily: "objektiv-mk2, sans-serif",
              position: "relative",
            }}
          >
            {message.type && (
              <div
                style={{
                  backgroundColor:
                    message.type === "success" ? "#d4edda" : "#f8d7da",
                  border: `1px solid ${
                    message.type === "success" ? "#c3e6cb" : "#f5c6cb"
                  }`,
                  color: message.type === "success" ? "#155724" : "#721c24",
                  display: "flex",
                  alignItems: "center",
                  borderRadius: "8px",
                  padding: "16px",
                  marginBottom: "20px",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  animation:
                    message.type === "success"
                      ? "successPulse 0.6s ease-in-out"
                      : "none",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", flex: 1 }}>
                  <div
                    style={{
                      backgroundColor:
                        message.type === "success" ? "#28a745" : "#dc3545",
                      color: "white",
                      borderRadius: "50%",
                      width: "24px",
                      height: "24px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginRight: "12px",
                      fontSize: "14px",
                      fontWeight: "bold",
                      flexShrink: 0,
                      minWidth: "24px",
                      minHeight: "24px",
                    }}
                  >
                    {message.type === "success" ? "✓" : "✗"}
                  </div>
                  <span
                    style={{
                      fontWeight: "500",
                      fontFamily: "objektiv-mk2, sans-serif",
                      fontSize: message.type === "success" ? "16px" : "14px",
                    }}
                  >
                    {message.text}
                  </span>
                </div>
              </div>
            )}

            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "500",
                  fontFamily: "objektiv-mk2, sans-serif",
                }}
              >
                Instructions — please read carefully:
              </label>
              <div
                style={{
                  padding: "12px 14px",
                  backgroundColor: "#f5f5f5",
                  borderLeft: "4px solid #4e85af",
                  borderRadius: "4px",
                  fontSize: "14px",
                  fontFamily: "objektiv-mk2, sans-serif",
                  lineHeight: "1.6",
                }}
              >
                <ul style={{ margin: 0, paddingLeft: "20px" }}>
                  <li>
                    The image you upload must be the <strong>FINAL</strong>{" "}
                    high-resolution image.
                  </li>
                  <li>
                    The image size must be <strong>EXACTLY</strong>: Width
                    8.95&quot; × Height 7&quot;.
                  </li>
                  <li>JPG or PNG only (no PDFs, please).</li>
                </ul>
              </div>
            </div>

            <div
              style={{
                marginBottom: "20px",
                padding: "16px",
                backgroundColor: "#e8f4fc",
                border: "1px solid #4e85af",
                borderRadius: "8px",
              }}
            >
              <div
                style={{
                  marginBottom: "10px",
                  fontWeight: "500",
                  fontFamily: "objektiv-mk2, sans-serif",
                  fontSize: "14px",
                }}
              >
                Complete Download specs (PDF)
              </div>
              <Button
                variant="outlined"
                startIcon={<GetAppIcon />}
                onClick={downloadSpecsPDF}
                style={{
                  fontFamily: "objektiv-mk2, sans-serif",
                  textTransform: "none",
                  borderColor: "#4e85af",
                  color: "#4e85af",
                }}
              >
                Download PDF Specs
              </Button>
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "500",
                  fontFamily: "objektiv-mk2, sans-serif",
                }}
              >
                Apply this photo to:
              </label>
              <div
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  padding: "12px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  backgroundColor: "#fafafa",
                }}
              >
                {availableAreas.length === 0 ? (
                  <p
                    style={{
                      margin: 0,
                      fontSize: "13px",
                      color: "#666",
                      fontFamily: "objektiv-mk2, sans-serif",
                    }}
                  >
                    No areas found in navbarInfo.
                  </p>
                ) : (
                  availableAreas.map((area) => (
                    <label
                      key={area.mlsAreaID}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        width: "100%",
                        marginBottom: "8px",
                        fontFamily: "objektiv-mk2, sans-serif",
                        fontSize: "14px",
                        cursor: "pointer",
                        wordBreak: "break-word",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedAreaIDs.includes(area.mlsAreaID)}
                        onChange={() => toggleAreaSelection(area.mlsAreaID)}
                        style={{ flexShrink: 0 }}
                      />
                      {area.mlsNeighborhood}
                    </label>
                  ))
                )}
              </div>
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "500",
                  fontFamily: "objektiv-mk2, sans-serif",
                }}
              >
                Upload a Custom Back Page Photo:
              </label>
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() =>
                  fileInputRef.current && fileInputRef.current.click()
                }
                style={{
                  width: "95%",
                  padding: "20px",
                  border: `2px dashed ${isDragOver ? "#4e85af" : "#666"}`,
                  borderRadius: "8px",
                  backgroundColor: isDragOver ? "#f0f8ff" : "#fafafa",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  fontFamily: "objektiv-mk2, sans-serif",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: "100px",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    color: "#666",
                    fontSize: "14px",
                    marginBottom: "4px",
                  }}
                >
                  Drag & Drop Custom Back Page Photo here or click to browse
                </div>
                <div style={{ color: "#999", fontSize: "12px" }}>
                  Only JPG or PNG files are allowed
                </div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/jpg"
                onChange={handleFileChange}
                style={{ display: "none" }}
              />
              {formData.file && (
                <p
                  style={{
                    marginTop: "8px",
                    fontSize: "12px",
                    color: "#666",
                    fontFamily: "objektiv-mk2, sans-serif",
                  }}
                >
                  Selected: {formData.file.name}
                </p>
              )}
            </div>

            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <button
                onClick={closeDialog}
                style={{
                  padding: "10px 20px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  backgroundColor: "white",
                  cursor: "pointer",
                  fontFamily: "objektiv-mk2, sans-serif",
                }}
              >
                Cancel
              </button>
              <button
                onClick={submitTicket}
                disabled={isSubmitting || !formData.file}
                style={{
                  padding: "10px 20px",
                  border: "none",
                  borderRadius: "4px",
                  backgroundColor: "#4e85af",
                  color: "white",
                  cursor:
                    isSubmitting || !formData.file ? "not-allowed" : "pointer",
                  opacity: isSubmitting || !formData.file ? 0.6 : 1,
                  fontFamily: "objektiv-mk2, sans-serif",
                  marginLeft: "-32px",
                }}
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </button>
              <div></div>
            </div>
          </div>
        </MailProDialogBody>
      </MailProDialog>
    </>
  );
};

export default React.memo(BackPageFreshdeskUploadButton);
